/** Subconjunto de `discountSummary` del order-service para el host del checkout web. */
export type HostDiscountSummary = {
  itemsTotal: number
  discountsTotal: number
  couponsTotal: number
  promotionsTotal: number
  promotions: { id: string; name: string; description: string; discount: number }[]
  coupons: { id: string; name: string; description: string; discount: number; couponKey: string }[]
}

const line = (
  raw: unknown,
): { id: string; name: string; description: string; discount: number } | null => {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const discount = row.discount
  const name = row.name
  if (typeof discount !== 'number' || !Number.isFinite(discount) || typeof name !== 'string') {
    return null
  }
  return {
    id: typeof row.id === 'string' ? row.id : '',
    name,
    description: typeof row.description === 'string' ? row.description : '',
    discount,
  }
}

const couponLine = (
  raw: unknown,
): { id: string; name: string; description: string; discount: number; couponKey: string } | null => {
  const base = line(raw)
  if (!base) return null
  const row = raw as Record<string, unknown>
  return {
    ...base,
    couponKey: typeof row.couponKey === 'string' ? row.couponKey : '',
  }
}

/** Normaliza la respuesta JSON de `pre-initiate` / `installment-promo/sync`. */
export const parseHostDiscountSummary = (raw: unknown): HostDiscountSummary | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const num = (key: string): number | undefined => {
    const value = o[key]
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined
  }
  const itemsTotal = num('itemsTotal')
  const discountsTotal = num('discountsTotal')
  const couponsTotal = num('couponsTotal')
  const promotionsTotal = num('promotionsTotal')
  if (
    itemsTotal === undefined ||
    discountsTotal === undefined ||
    couponsTotal === undefined ||
    promotionsTotal === undefined
  ) {
    return undefined
  }
  const promotions = Array.isArray(o.promotions)
    ? o.promotions.flatMap((entry) => {
        const parsed = line(entry)
        return parsed ? [parsed] : []
      })
    : []
  const coupons = Array.isArray(o.coupons)
    ? o.coupons.flatMap((entry) => {
        const parsed = couponLine(entry)
        return parsed ? [parsed] : []
      })
    : []
  return {
    itemsTotal,
    discountsTotal,
    couponsTotal,
    promotionsTotal,
    promotions,
    coupons,
  }
}
